import React, { useMemo } from 'react';
import NewBaseTable from 'components/NewBaseTable/NewBaseTable';
import { OrderTableWrapper } from './NewSFOrdersTableStyled';
import { SFOrderHistory, TwoWayOrderHistoryData } from 'types';
import { generateColumnConfigs, generateTwoWayColumnConfigs } from './OrderUtility';
import { Pagination } from 'components';
import { ITEMS_PER_PAGE } from '.';
import { SipTableContainer } from 'common/pages/fleetV2/machine/aseptic/views/MachineHealth/components/SipTableWidget/SipTableWidget.elements';
import { Product } from 'types/parts';
import styled from 'styled-components';
import { ALL_FILTER_VALUE } from './constants';
import { set } from 'lodash';

const TwoWayOrderHistoryTableContainer = styled(SipTableContainer)`
  &.sip-table-widget {
    .toolbar-wrapper {
      display: flex;
    }
  }
`;

interface Props {
  sfOrders: SFOrderHistory[];
  addItemsToCart: (order: TwoWayOrderHistoryData) => void;
  showOrderModal: (order: TwoWayOrderHistoryData) => void;
  formatPODate: (date: string) => string;
  updateTableOnFilterChange?: (filter: string) => void;
  isDataFetching?: boolean;
  t: (key: string) => string;
  totalRecords: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  setOrderSearchKey?: (searchKey: string) => void;
  setOrderFilter?: (filter: string) => void;
}
const defaultSortState = {
  id: 'orderNumber',
  desc: true
};

const normalizeStatus = (value?: string): string => value?.trim().toLowerCase() || '';

const addProductsToSubRows = (sfOrders: SFOrderHistory[]): TwoWayOrderHistoryData[] => {
  return sfOrders.map((order) => ({
    ...order,
    orderDate: order.poDate,
    status: order.poStatus,
    productCount: order.poDetails ? order.poDetails.length : 0,
    products: order.poDetails
      ? (order.poDetails.map((part) => ({
          ...part,
          sku: part.partNumber,
          leadTime: part.leadtime ? Number(part.leadtime) : 0,
          stock: part.stock ? Number(part.stock) : 0,
          unitPrice: part.unitPrice ? Number(part.unitPrice) : 0
        })) as Product[])
      : [],
    subRows: order.poDetails
      ? [
          {
            newTable: order.poDetails.map((product) => ({
              id: product.productId,
              name: product.description,
              description: product.description,
              sku: product.partNumber,
              leadTime: product.leadTime,
              stock: product.stock,
              subtotal: product.subtotal,
              quantity: product.quantity,
              urgent: product.urgent,
              tentativeDeliveryDate: product.tentativeDeliveryDate,
              price: product.unitPrice as string,
              priceUnit: product.currency as string,
              orderStatus: order.poStatus,
            })),
            type: 'twowayorder'
          }
        ]
      : []
  }));

};
const TwoWayOrderHistory = ({
  sfOrders,
  addItemsToCart,
  showOrderModal,
  formatPODate,
  isDataFetching,
  t,
  totalRecords,
  currentPage,
  setCurrentPage,
  setOrderSearchKey,
  setOrderFilter,
}: Props): JSX.Element => {
  const [showPagination, setShowPagination] = React.useState(true);
  const [selectedStatusFilter, setSelectedStatusFilter] = React.useState(ALL_FILTER_VALUE);

  const updateSearchValue = (inputValue?: string) => {
    if (inputValue && inputValue.length > 0) {
      setShowPagination(false);
      setOrderSearchKey?.(inputValue);
    } else {
      setShowPagination(true);
      setOrderSearchKey?.('');
    }
  };

  const handleFilterRequest = (status: string) => {
    setSelectedStatusFilter(status || ALL_FILTER_VALUE);
    setOrderFilter?.(status || '');
  };

  const twoWayOrder = useMemo(() => sfOrders && addProductsToSubRows(sfOrders), [sfOrders]);
  const normalizedSelectedStatusFilter = useMemo(
    () => normalizeStatus(selectedStatusFilter),
    [selectedStatusFilter]
  );

  const filteredTwoWayOrder = useMemo(() => {
    if (selectedStatusFilter === ALL_FILTER_VALUE) {
      return twoWayOrder;
    }

    return twoWayOrder.filter((order) => normalizeStatus(order.status) === normalizedSelectedStatusFilter);
  }, [normalizedSelectedStatusFilter, selectedStatusFilter, twoWayOrder]);

  return (
    <>
      <TwoWayOrderHistoryTableContainer className="widget-ui__main sip-table-widget">
        <NewBaseTable
          newTableData={filteredTwoWayOrder}
          columnConfigs={generateTwoWayColumnConfigs({
            addItemsToCart,
            showOrderModal,
            formatPODate,
            t
          })}
          sortState={defaultSortState}
          enableFilter={true}
          isShowGlobalSearch={true}
          isShowColumnOptions={true}
          handleFilterRequest={handleFilterRequest}
          isDataLoading={isDataFetching}
          tdMarginLeft="0.625rem"
          textAlign="left"
          searchInputValue={updateSearchValue}
          hasNestedRows={true}
        />
      </TwoWayOrderHistoryTableContainer>
      {showPagination && filteredTwoWayOrder && filteredTwoWayOrder.length > 0 && (
        <Pagination
          onPageChange={(page) => setCurrentPage(page)}
          itemsPerPage={ITEMS_PER_PAGE}
          numItems={totalRecords}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default TwoWayOrderHistory;
